import request from '@/util/request'
import Util from '@/util'
import _ from 'lodash'
import {optionsToPaginationParams} from '@/util/pagination'
import {
  RoleGroups,
  TradingRoles,
  BankTranTypes,
} from '@/util/constants'
import {default as guardsMixin} from '@/mixins/guards'
import {filterPreSettlementPaymentMethods} from '@/util/preSettlement'

const COMPANY_MODEL = {
  id: null,
  active: null,
  name: null,
  netsuite_internalId: null,
  url: null,
  email: null,
  altEmail1: null,
  altEmail2: null,
  altEmail3: null,
  altEmail4: null,
  altEmail5: null,
  city: null,
  state: null,
  zipcode: null,
  phoneNumber: null,
  alternatePhoneNumber: null,
  faxnumber: null,
  mobileNumber: null,
  isCustomer: null,
  isVendor: null,
  entityStatus: null,
  shipSecure: null,
  creditLimit: null,
  ucgCategory: null,
  availableCredit: null,
  preSettlementAgreementDate: null,
  preSettlementAgreementId: null,
  preSettlementEligible: null,
  preSettlementCanUse: null,
  preSettlementEnabled: false,
  presentExposure: null,
  exposureLimit: 0,
  equityTrustContactId: null,
  equityTrustPaymentMethodId: null,
  enhancedMode: false,
  logoPath: null,
  markupBidPremium: 0,
  markupBidPriceFormat: `$`,
  markupAskPremium: 0,
  markupAskPriceFormat: `$`,
  retailTicketHeader: null,
  retailTicketFooter: null,
  directDeposit: null,
  directDebit: null,
  contactList: [],
  defaultShippingInstructionId: null,
  enableLimitOrders: false,
  companyBankAccounts: [],
}

const EMAIL_PREF_OPTIONS = [ // TODO: be able to grab these from the NS api itself, or store in API instead?
  {
    id: 'no_fulfill',
    label: 'SO Fullfillment Notif Opt Out',
    description: null // TODO: provide mouseover explanation
  },
  {
    id: 'no_inv_paid',
    label: 'Invoice Paid In Full Opt Out',
    description: null
  },
  {
    id: 'no_open_inv',
    label: 'Open Invoice Opt Out',
    description: null
  },
  {
    id: 'no_po_confirmed',
    label: 'PO Record Confirmed Opt Out',
    description: null
  },
  {
    id: 'no_po_received',
    label: 'PO Received Notification Opt Out',
    description: null
  },
  {
    id: 'no_po_update_email',
    label: 'PO Record Updated Opt Out',
    description: null
  },
  {
    id: 'no_rma_confirm',
    label: 'RMA Record Confirmed Opt Out',
    description: null
  },
  {
    id: 'no_ship_notif',
    label: 'SO Shipping Notifications Opt Out',
    description: null
  },
  {
    id: 'no_so_confirmed',
    label: 'SO Record Confirmed Opt Out',
    description: null
  },
  {
    id: 'no_so_update',
    label: 'SO Record Updated Opt Out',
    description: null
  },
  {
    id: 'no_vpp_bill_pay',
    label: 'PO Paid Notification Opt Out',
    description: null
  },
  {
    id: 'no_vra_confirm',
    label: 'VRA Record Confirmed Opt Out',
    description: null
  }
]

const state = {
  selected_company: Object.assign({}, COMPANY_MODEL),
  selected_company_addresses: {
    data: [],
    page: null,
    nextPage: null,
  },
  selected_company_email_prefs: {},
  selected_company_roles: [],
  trading_roles: [],
  selected_trading_role: null,
  recent_address: {
    id: null,
  },
  default_billing_address: {
    id: null,
  },
  default_shipping_address: {
    id: null,
  },
  warehouse_locations: [],
  depository_locations: [],
  companies: [],
  bank_infos: [],
  subscriptionCategories: [],
}

const getPrimaryBankAccountByTranType = (state, tranType) => {
  const match = state.selected_company.companyBankAccounts.filter(
    (x) => x.tranType == tranType && x.primary
  )
  return _.get(match, `[0]`, null)
}

const getters = {
  getCompanies: (state) => state.companies,
  getSelectedCompany: (state) => state.selected_company,
  getPrimaryDebitBankAccount: (state) =>
    getPrimaryBankAccountByTranType(state, BankTranTypes.DEBIT),
  getPrimaryDepositBankAccount: (state) =>
    getPrimaryBankAccountByTranType(state, BankTranTypes.DEPOSIT),
  getBankInfos: (state) => state.bank_infos,
  getSelectedCompanyAddresses: (state) => state.selected_company_addresses,
  getSelectedCompanyEmailPrefs: (state) => state.selected_company_email_prefs,
  getRecentAddress: (state) => state.recent_address,
  getDefaultBillingAddress: (state) => state.default_billing_address,
  getDefaultShippingAddress: (state) => state.default_shipping_address,
  getWarehouseLocations: (state) => state.warehouse_locations,
  getDepositoryLocations: (state) => state.depository_locations,
  getSubscriptionCategories: (state) => state.subscriptionCategories,
  getPaymentMethodsByBuySell: (state, getters, rootState, rootGetters) => (buySell) => {
    if (buySell === 'sell') {
      const paymentMethods = _.get(
        state,
        `selected_company.paymentMethodTier.po_payment_methods`,
        []
      )
      return filterPreSettlementPaymentMethods(
        paymentMethods,
        state.selected_company,
        rootGetters.getConfig('PRE_SETTLEMENT_TERM_IDS')
      )
    } else {
      return _.get(
        state,
        `selected_company.paymentMethodTier.so_payment_methods`,
        []
      )
    }
  },
  getSelectedCompanyRoles: (state) => state.selected_company_roles,
  getTradingRoles: (state) => state.trading_roles,
  getSelectedTradingRole: (state) => state.selected_trading_role,
}

const actions = {
  fetchCompanyAnalytics({getters}, companyId) {
    return request({
      url: `/companies/${companyId}/analytics`,
      method: 'get',
    })
  },
  fetchCompanyDashboard({getters}, companyId) {
    return request({
      url: `/companies/${companyId}/dashboard`,
      method: 'get',
    })
  },
  fetchCompanyDealerSheet({getters}, companyId) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${selectedCompanyId}/dealer-sheet`,
      method: 'get',
    })
  },
  updateCompanyUsers({getters, commit, dispatch}, data) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${selectedCompanyId}/users`,
      data,
      method: 'patch',
    }).then((data) => {
      dispatch(`refreshSelectedCompany`)
    })
  },
  fetchBankInfos({getters, commit, dispatch}) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: `/company-bank-info/${selectedCompanyId}`,
      method: 'get',
    }).then((data) => {
      commit('SET_BANK_INFOS', data)
      dispatch(`refreshSelectedCompany`)
    })
  },
  submitBankingInformation({getters, dispatch}, data) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: `/company-bank-info/${selectedCompanyId}`,
      data,
      method: 'post',
    }).then(() => {
      dispatch(`refreshSelectedCompany`)
    })
  },
  setSelectedTradingRole({getters, commit}, tradingRole) {
    if (
      getters.getTradingRoles.map((x) => x.value).indexOf(tradingRole) === -1
    ) {
      throw Error(
        `Unable to provided role. ${tradingRole} is not a valid option.`
      )
    }
    commit('SET_SELECTED_TRADING_ROLE', tradingRole)
  },
  fetchSubscriptionCategories({commit}) {
    return request({
      url: '/subscription-categories/?limit=1000',
      method: 'get',
    }).then((data) => {
      commit('SET_SUBSCRIPTION_CATEGORIES', data.data)
    })
  },
  fetchCompanies({commit}, fetchRequest = {}) {
    const {options = {}, query = {}} = fetchRequest
    const paginationParams = optionsToPaginationParams(options)
    const params = {query, ...paginationParams}
    return request({
      url: '/companies/',
      params,
      method: 'get',
    })
      .then((data) => {
        commit('SET_COMPANIES', data)
        return data
      })
  },
  fetchAndSelectCompany({commit, dispatch, getters}, selectedCompanyId) {
    return request({
      url: '/companies/' + selectedCompanyId,
      method: 'get',
    }).then((data) => {
      const previousCompanyId = getters.getSelectedCompany?.id
      if (previousCompanyId && previousCompanyId !== data.id) {
        dispatch('clearAccountSummary', null, {root: true})
      }
      // Reset carts buy preserve items
      dispatch('resetCartState', {buySell: `buy`, preserveItems: true})
      dispatch('resetCartState', {buySell: `sell`, preserveItems: true})
      commit('SET_SELECTED_COMPANY', data)
    })
  },
  refreshSelectedCompany({getters, commit}) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: '/companies/' + selectedCompanyId,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_COMPANY', data)
    })
  },
  async recordRefineryAgreement({ getters, dispatch }, { agreementId }) {
    const companyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${companyId}/refinery-agreement`,
      method: 'post',
      data: { agreementId },
    })
    // Ensure company flags are updated locally
    await dispatch('refreshSelectedCompany')
  },
  async acceptPreSettlementTerms({getters, dispatch}, {agreementId} = {}) {
    if (!agreementId) {
      throw new Error('agreementId is required to accept Pre-Settlement terms.')
    }
    const companyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${companyId}/pre-settlement-terms`,
      method: 'post',
      data: {agreementId},
    })
    await dispatch('refreshSelectedCompany')
  },
  async revokeRefineryAgreement({ getters, dispatch }) {
    const companyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${companyId}/refinery-agreement`,
      method: 'delete',
    })
    // Ensure company flags are updated locally
    await dispatch('refreshSelectedCompany')
  },
  fetchDefaultSelectedCompany({getters, commit}) {
    // @todo - Make company selectable if user has access to multiple
    const selectedCompanyId = Object.keys(
      getters.getUserProfile.companyPermissions
    )[0]
    commit(
      'SET_SELECTED_COMPANY_ROLES',
      getters.getUserProfile.companyPermissions[selectedCompanyId].roles.map(
        (role) => role.role
      )
    )
    return request({
      url: '/companies/' + selectedCompanyId,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_COMPANY', data)
    })
  },
  fetchWarehouseLocations({getters, commit}) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: '/warehouse-locations/' + selectedCompanyId,
      method: 'get',
    }).then((data) => {
      commit('SET_WAREHOUSE_LOCATIONS', data.data)
    })
  },
  fetchDepositoryLocations({getters, commit}) {
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: `/depositories/${selectedCompanyId}`,
      method: 'get',
    }).then((data) => {
      commit('SET_DEPOSITORY_LOCATIONS', data)
    })
  },
  async fetchSelectedCompanyAddresses({getters, commit}, options) {
    const paginationParams = optionsToPaginationParams(options)
    const selectedCompanyId = getters.getSelectedCompany.id
    if (selectedCompanyId === undefined || selectedCompanyId === null) {
      commit('SET_SELECTED_COMPANY_ADDRESSES', [])
      return Promise.resolve(null)
    }
    const params = {
      ...paginationParams,
      search: options.search || undefined
    }

    return request({
      url: '/company-locations/' + selectedCompanyId,
      params: params,
      method: 'get',
    }).then((data) => {
      commit('SET_SELECTED_COMPANY_ADDRESSES', data)
    })
  },
  async deleteCompanyAddress(context, companyAddressId) {
    await request({
      url: `/company-locations/${companyAddressId}?ui=true`,
      method: 'delete',
    })
  },
  async fetchSelectedCompanyEmailPrefs({getters, commit}) {
    const selectedCompanyId = getters.getSelectedCompany.id
    if (selectedCompanyId === undefined || selectedCompanyId === null) {
      commit('SET_SELECTED_COMPANY_EMAIL_PREFS', {})
      return Promise.resolve(null)
    }
    return request({
      url: `/companies/${selectedCompanyId}/email-preferences`,
      method: 'get',
    }).then((data) => {
      const selectedEmailPrefs = EMAIL_PREF_OPTIONS
        .filter(option => Object.keys(data).includes(option.id))
        .map(({id, label, description}) => {
        return {
          id,
          title: label,
          description,
          selected: Boolean(data[id]),
        }
      });
      commit('SET_SELECTED_COMPANY_EMAIL_PREFS', selectedEmailPrefs)
    })
  },
  async updateEmailPreferences({dispatch, getters}, data) {
    const selectedCompanyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${selectedCompanyId}/email-preferences`,
      data,
      method: 'post',
    })
    return dispatch(`fetchSelectedCompanyEmailPrefs`)
  },
  async syncCompanyOrders(context, selectedCompanyNetsuiteId) {
    if (!selectedCompanyNetsuiteId) {
      throw new Error(`Unable to locate selected company internal id.`)
    }
    await request({
      url: `/jobs/company-orders-refresh/${selectedCompanyNetsuiteId}`,
      method: 'post',
    })
  },
  async setCompanyEquityTrustContactId({dispatch, getters}, contactId) {
    const selectedCompanyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${selectedCompanyId}/et/contactId`,
      data: {contactId},
      method: 'put',
    })
    await dispatch(`refreshSelectedCompany`)
  },
  async setCompanyMarkups(
    {dispatch, getters},
    {
      markupBidPremium,
      markupBidPriceFormat,
      markupAskPremium,
      markupAskPriceFormat,
    }
  ) {
    const selectedCompanyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${selectedCompanyId}/markups`,
      data: {
        markupBidPremium,
        markupBidPriceFormat,
        markupAskPremium,
        markupAskPriceFormat,
      },
      method: 'put',
    })
    return dispatch(`refreshSelectedCompany`)
  },
  async setCompanySettings({dispatch, getters}, data) {
    const selectedCompanyId = getters.getSelectedCompany.id
    await request({
      url: `/companies/${selectedCompanyId}/settings`,
      data,
      method: 'put',
    })
    return dispatch(`refreshSelectedCompany`)
  },
  async saveCompanyAddress({commit}, editedItem) {
    let location
    const data = {
      id: editedItem['location.id'],
      street1: editedItem['location.street1'],
      street2: editedItem['location.street2'],
      country: editedItem['location.country'] || `US`,
      city: editedItem['location.city'],
      zip: editedItem['location.zip'],
      phone: editedItem['location.phone'],
      placeId: '0',
      loc: null,
      defaultBilling: editedItem['defaultBilling'],
      defaultShipping: editedItem['defaultShipping'],
      companyLocationId: editedItem['id'],
      addressee: editedItem['location.addressee'],
      attention: editedItem['location.attention'],
      label: editedItem['location.label'],
      state: editedItem['location.state'],
      companyPin: editedItem['companyPin'],
      companyId: editedItem['companyId'],
    }
    await request({
      url: `/locations/from-place-id/${data.id}/0`,
      data,
      method: 'put',
    })
      .then((result) => {
        location = result.result
        const data2 = {
          companyId: editedItem['companyId'],
          defaultBilling: editedItem['defaultBilling'],
          defaultShipping: editedItem['defaultShipping'],
          displayName: editedItem['location.label'],
          id: editedItem['id'],
          locationId: result.result.id,
          title: editedItem['location.label'],
          ui: true,
          addToAddressBook: editedItem['addToAddressBook'],
        }
        return request({
          url: `/company-locations/companies/${data2.companyId}`,
          data: data2,
          method: 'post',
        })
      })
      .then((result) => {
        result.result.location = location
        commit('SET_RECENT_ADDRESS', result.result)
      })
  },
}

const mutations = {
  SET_COMPANIES(state, companies) {
    companies.data.rows = companies.data.rows.map((company) =>
      Util.filterByModel(company, COMPANY_MODEL)
    )
    state.companies = companies
  },
  SET_SELECTED_COMPANY(state, selectedCompany) {
    state.selected_company = selectedCompany
    this.commit(
      `SET_DEFAULT_BILLING_ADDRESS`,
      selectedCompany.defaultBillingAddress
    )
    this.commit(
      `SET_DEFAULT_SHIPPING_ADDRESS`,
      selectedCompany.defaultShippingAddress
    )
  },
  SET_SELECTED_COMPANY_ROLES(state, roles) {
    state.selected_company_roles = roles
    state.trading_roles = []
    const role_ids = roles.map((x) => x.netsuite_internalId)
    if (guardsMixin.methods.isAdmin()) {
      state.trading_roles.push({
        text: `Sales Manager`,
        value: TradingRoles.SALES_MANAGER,
      })
      state.trading_roles.push({
        text: `Sales Rep`,
        value: TradingRoles.SALES_REP,
      })
      state.trading_roles.push({
        text: `Authorized Trader`,
        value: TradingRoles.AUTHORIZED_TRADER,
      })
      state.selected_trading_role = TradingRoles.SALES_MANAGER
    } else if (
      role_ids.filter((value) =>
        RoleGroups.SALES_MANAGER_ROLE_IDS.includes(value)
      ).length > 0
    ) {
      state.trading_roles.push({
        text: `Sales Manager`,
        value: TradingRoles.SALES_MANAGER,
      })
      state.trading_roles.push({
        text: `Sales Rep`,
        value: TradingRoles.SALES_REP,
      })
      state.selected_trading_role = TradingRoles.SALES_MANAGER
    } else if (
      role_ids.filter((value) => RoleGroups.SALES_REP_ROLE_IDS.includes(value))
        .length > 0
    ) {
      state.trading_roles.push({
        text: `Sales Rep`,
        value: TradingRoles.SALES_REP,
      })
      state.selected_trading_role = TradingRoles.SALES_REP
    } else {
      state.trading_roles.push({
        text: `Authorized Trader`,
        value: TradingRoles.AUTHORIZED_TRADER,
      })
      state.selected_trading_role = TradingRoles.AUTHORIZED_TRADER
    }
  },
  SET_SELECTED_TRADING_ROLE(state, tradingRole) {
    state.selected_trading_role = tradingRole
  },
  SET_SELECTED_COMPANY_ADDRESSES(state, selectedCompanyAddresses) {
    state.selected_company_addresses = selectedCompanyAddresses
  },
  SET_SELECTED_COMPANY_EMAIL_PREFS(state, selectedCompanyEmailPrefs) {
    state.selected_company_email_prefs = selectedCompanyEmailPrefs
  },
  SET_RECENT_ADDRESS(state, recentAddress) {
    state.recent_address = recentAddress
  },
  SET_DEFAULT_BILLING_ADDRESS(state, address) {
    state.default_billing_address = address
  },
  SET_DEFAULT_SHIPPING_ADDRESS(state, address) {
    state.default_shipping_address = address
  },
  SET_WAREHOUSE_LOCATIONS(state, warehouseLocations) {
    state.warehouse_locations = warehouseLocations
  },
  SET_DEPOSITORY_LOCATIONS(state, depositoryLocations) {
    state.depository_locations = depositoryLocations
  },
  SET_SUBSCRIPTION_CATEGORIES(state, subscriptionCategories) {
    state.subscriptionCategories = subscriptionCategories
  },
  SET_BANK_INFOS(state, bankInfos) {
    state.bank_infos = bankInfos
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
}
